import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface SafetyAlert {
  id: string;
  type: 'content_flagged' | 'user_reported' | 'suspicious_behavior' | 'safety_reminder' | 'location_warning';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message: string;
  timestamp: Date;
  userId?: string;
  contentId?: string;
  actions?: SafetyAction[];
  isRead: boolean;
  isResolved: boolean;
}

export interface SafetyAction {
  id: string;
  label: string;
  type: 'block' | 'report' | 'hide' | 'restrict' | 'warn' | 'dismiss';
  severity: 'low' | 'medium' | 'high';
}

export interface UserReport {
  id: string;
  reporterId: string;
  reportedUserId: string;
  reason: string;
  category: 'harassment' | 'inappropriate_content' | 'fake_profile' | 'spam' | 'underage' | 'other';
  description: string;
  evidence?: string[];
  timestamp: Date;
  status: 'pending' | 'investigating' | 'resolved' | 'dismissed';
  moderatorNotes?: string;
}

export interface ContentModeration {
  id: string;
  contentType: 'message' | 'photo' | 'profile' | 'bio';
  contentId: string;
  userId: string;
  flaggedReason: string[];
  confidence: number; // 0-1 confidence score from AI
  status: 'pending' | 'approved' | 'rejected' | 'needs_review';
  moderatorId?: string;
  timestamp: Date;
  aiAnalysis?: {
    nudity: number;
    violence: number;
    hate_speech: number;
    spam: number;
    underage: number;
  };
}

export interface SafetySettings {
  contentFiltering: {
    enabled: boolean;
    strictMode: boolean;
    hideExplicitContent: boolean;
    blurSensitiveImages: boolean;
  };
  locationSafety: {
    shareLocationEnabled: boolean;
    shareOnlyWithMatches: boolean;
    hideExactLocation: boolean;
    locationReminders: boolean;
  };
  communicationSafety: {
    blockUnverifiedUsers: boolean;
    requirePhoneVerification: boolean;
    autoDeleteUnverifiedChats: boolean;
    linkPreventionEnabled: boolean;
  };
  meetupSafety: {
    publicPlaceReminders: boolean;
    friendNotificationEnabled: boolean;
    emergencyContactEnabled: boolean;
    safeWordEnabled: boolean;
  };
  privacySettings: {
    hideFromSearch: boolean;
    hideLastSeen: boolean;
    hideReadReceipts: boolean;
    restrictUnverifiedContacts: boolean;
  };
}

interface SafetyContextType {
  alerts: SafetyAlert[];
  reports: UserReport[];
  contentModerations: ContentModeration[];
  safetySettings: SafetySettings;
  
  // Alert management
  createAlert: (alert: Omit<SafetyAlert, 'id' | 'timestamp' | 'isRead' | 'isResolved'>) => void;
  markAlertAsRead: (alertId: string) => void;
  resolveAlert: (alertId: string) => void;
  dismissAlert: (alertId: string) => void;
  
  // Reporting system
  reportUser: (reportedUserId: string, reason: string, category: UserReport['category'], description: string, evidence?: string[]) => Promise<void>;
  reportContent: (contentType: ContentModeration['contentType'], contentId: string, reason: string[]) => Promise<void>;
  
  // Content moderation
  flagContent: (contentType: string, contentId: string, reasons: string[]) => Promise<void>;
  moderateContent: (moderationId: string, decision: 'approve' | 'reject', notes?: string) => Promise<void>;
  
  // Safety actions
  blockUser: (userId: string, reason?: string) => Promise<void>;
  unblockUser: (userId: string) => Promise<void>;
  hideContent: (contentId: string) => Promise<void>;
  restrictUser: (userId: string, duration: number, reason: string) => Promise<void>;
  
  // Safety settings
  updateSafetySettings: (settings: Partial<SafetySettings>) => void;
  
  // Safety checks
  isUserBlocked: (userId: string) => boolean;
  isContentHidden: (contentId: string) => boolean;
  shouldWarnUser: (action: string, context: any) => SafetyAlert | null;
  
  // Statistics
  getUnreadAlertsCount: () => number;
  getPendingReportsCount: () => number;
  getSafetyScore: () => number; // 0-100 safety score
}

const SafetyContext = createContext<SafetyContextType | undefined>(undefined);

export const useSafety = () => {
  const context = useContext(SafetyContext);
  if (!context) {
    throw new Error('useSafety must be used within SafetyProvider');
  }
  return context;
};

interface SafetyProviderProps {
  children: ReactNode;
  currentUserId: string;
}

export const SafetyProvider: React.FC<SafetyProviderProps> = ({ children, currentUserId }) => {
  const [alerts, setAlerts] = useState<SafetyAlert[]>([]);
  const [reports, setReports] = useState<UserReport[]>([]);
  const [contentModerations, setContentModerations] = useState<ContentModeration[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<Set<string>>(new Set());
  const [hiddenContent, setHiddenContent] = useState<Set<string>>(new Set());
  const [safetySettings, setSafetySettings] = useState<SafetySettings>({
    contentFiltering: {
      enabled: true,
      strictMode: false,
      hideExplicitContent: false,
      blurSensitiveImages: true
    },
    locationSafety: {
      shareLocationEnabled: false,
      shareOnlyWithMatches: true,
      hideExactLocation: true,
      locationReminders: true
    },
    communicationSafety: {
      blockUnverifiedUsers: false,
      requirePhoneVerification: false,
      autoDeleteUnverifiedChats: false,
      linkPreventionEnabled: true
    },
    meetupSafety: {
      publicPlaceReminders: true,
      friendNotificationEnabled: false,
      emergencyContactEnabled: false,
      safeWordEnabled: false
    },
    privacySettings: {
      hideFromSearch: false,
      hideLastSeen: false,
      hideReadReceipts: false,
      restrictUnverifiedContacts: false
    }
  });

  // Initialize with some sample data
  useEffect(() => {
    const sampleAlerts: SafetyAlert[] = [
      {
        id: 'alert_1',
        type: 'safety_reminder',
        severity: 'low',
        title: 'Meeting Safety Reminder',
        message: 'Remember to meet in a public place for your first date tonight.',
        timestamp: new Date(Date.now() - 3600000), // 1 hour ago
        isRead: false,
        isResolved: false,
        actions: [
          { id: 'ack', label: 'Got it', type: 'dismiss', severity: 'low' },
          { id: 'tips', label: 'Safety Tips', type: 'warn', severity: 'low' }
        ]
      },
      {
        id: 'alert_2',
        type: 'suspicious_behavior',
        severity: 'medium',
        title: 'Unusual Activity Detected',
        message: 'A user you\'re chatting with has been reported by multiple users.',
        timestamp: new Date(Date.now() - 7200000), // 2 hours ago
        userId: 'user_suspicious_1',
        isRead: false,
        isResolved: false,
        actions: [
          { id: 'block', label: 'Block User', type: 'block', severity: 'high' },
          { id: 'report', label: 'Report User', type: 'report', severity: 'medium' },
          { id: 'dismiss', label: 'Dismiss', type: 'dismiss', severity: 'low' }
        ]
      }
    ];

    setAlerts(sampleAlerts);

    // Simulate periodic safety checks
    const safetyInterval = setInterval(() => {
      if (Math.random() < 0.1) { // 10% chance every interval
        generateSafetyReminder();
      }
    }, 30000); // Every 30 seconds

    return () => clearInterval(safetyInterval);
  }, []);

  const generateSafetyReminder = () => {
    const reminders = [
      {
        type: 'location_warning' as const,
        severity: 'medium' as const,
        title: 'Location Sharing Alert',
        message: 'You\'re sharing your location with multiple users. Consider reviewing your privacy settings.'
      },
      {
        type: 'safety_reminder' as const,
        severity: 'low' as const,
        title: 'Safety Check-in',
        message: 'It\'s been a while since your last safety review. Take a moment to check your settings.'
      },
      {
        type: 'content_flagged' as const,
        severity: 'medium' as const,
        title: 'Content Review',
        message: 'Some of your recent messages have been flagged by our safety system.'
      }
    ];

    const reminder = reminders[Math.floor(Math.random() * reminders.length)];
    createAlert(reminder);
  };

  const createAlert = (alertData: Omit<SafetyAlert, 'id' | 'timestamp' | 'isRead' | 'isResolved'>) => {
    const newAlert: SafetyAlert = {
      ...alertData,
      id: `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      isRead: false,
      isResolved: false
    };

    setAlerts(prev => [newAlert, ...prev]);
  };

  const markAlertAsRead = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, isRead: true } : alert
    ));
  };

  const resolveAlert = (alertId: string) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, isResolved: true, isRead: true } : alert
    ));
  };

  const dismissAlert = (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const reportUser = async (reportedUserId: string, reason: string, category: UserReport['category'], description: string, evidence?: string[]) => {
    const newReport: UserReport = {
      id: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      reporterId: currentUserId,
      reportedUserId,
      reason,
      category,
      description,
      evidence,
      timestamp: new Date(),
      status: 'pending'
    };

    setReports(prev => [newReport, ...prev]);

    // Create alert for successful report
    createAlert({
      type: 'user_reported',
      severity: 'low',
      title: 'Report Submitted',
      message: `Your report about user ${reportedUserId} has been submitted and is being reviewed.`,
      actions: [
        { id: 'ok', label: 'OK', type: 'dismiss', severity: 'low' }
      ]
    });

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
  };

  const reportContent = async (contentType: ContentModeration['contentType'], contentId: string, reasons: string[]) => {
    const newModeration: ContentModeration = {
      id: `mod_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      contentType,
      contentId,
      userId: 'unknown', // Would be populated from content lookup
      flaggedReason: reasons,
      confidence: 0.8,
      status: 'pending',
      timestamp: new Date(),
      aiAnalysis: {
        nudity: Math.random(),
        violence: Math.random(),
        hate_speech: Math.random(),
        spam: Math.random(),
        underage: Math.random()
      }
    };

    setContentModerations(prev => [newModeration, ...prev]);

    createAlert({
      type: 'content_flagged',
      severity: 'medium',
      title: 'Content Reported',
      message: `Content has been flagged for review: ${reasons.join(', ')}`,
      contentId,
      actions: [
        { id: 'hide', label: 'Hide Content', type: 'hide', severity: 'medium' },
        { id: 'ok', label: 'OK', type: 'dismiss', severity: 'low' }
      ]
    });
  };

  const flagContent = async (contentType: string, contentId: string, reasons: string[]) => {
    await reportContent(contentType as ContentModeration['contentType'], contentId, reasons);
  };

  const moderateContent = async (moderationId: string, decision: 'approve' | 'reject', notes?: string) => {
    setContentModerations(prev => prev.map(mod => 
      mod.id === moderationId 
        ? { 
            ...mod, 
            status: decision === 'approve' ? 'approved' : 'rejected',
            moderatorId: currentUserId,
            moderatorNotes: notes
          }
        : mod
    ));
  };

  const blockUser = async (userId: string, reason?: string) => {
    setBlockedUsers(prev => new Set(prev).add(userId));
    
    createAlert({
      type: 'user_reported',
      severity: 'medium',
      title: 'User Blocked',
      message: `User ${userId} has been blocked. They can no longer contact you.`,
      userId,
      actions: [
        { id: 'unblock', label: 'Unblock', type: 'dismiss', severity: 'low' }
      ]
    });
  };

  const unblockUser = async (userId: string) => {
    setBlockedUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(userId);
      return newSet;
    });
  };

  const hideContent = async (contentId: string) => {
    setHiddenContent(prev => new Set(prev).add(contentId));
  };

  const restrictUser = async (userId: string, duration: number, reason: string) => {
    createAlert({
      type: 'user_reported',
      severity: 'high',
      title: 'User Restricted',
      message: `User ${userId} has been restricted for ${duration} hours. Reason: ${reason}`,
      userId
    });
  };

  const updateSafetySettings = (settings: Partial<SafetySettings>) => {
    setSafetySettings(prev => ({
      ...prev,
      ...settings,
      contentFiltering: { ...prev.contentFiltering, ...settings.contentFiltering },
      locationSafety: { ...prev.locationSafety, ...settings.locationSafety },
      communicationSafety: { ...prev.communicationSafety, ...settings.communicationSafety },
      meetupSafety: { ...prev.meetupSafety, ...settings.meetupSafety },
      privacySettings: { ...prev.privacySettings, ...settings.privacySettings }
    }));
  };

  const isUserBlocked = (userId: string): boolean => {
    return blockedUsers.has(userId);
  };

  const isContentHidden = (contentId: string): boolean => {
    return hiddenContent.has(contentId);
  };

  const shouldWarnUser = (action: string, context: any): SafetyAlert | null => {
    // Example safety warnings
    if (action === 'share_location' && !safetySettings.locationSafety.shareLocationEnabled) {
      return {
        id: `warning_${Date.now()}`,
        type: 'location_warning',
        severity: 'medium',
        title: 'Location Sharing Warning',
        message: 'You\'re about to share your location. Make sure you trust this person.',
        timestamp: new Date(),
        isRead: false,
        isResolved: false,
        actions: [
          { id: 'proceed', label: 'Share Anyway', type: 'dismiss', severity: 'medium' },
          { id: 'cancel', label: 'Cancel', type: 'dismiss', severity: 'low' }
        ]
      };
    }

    if (action === 'send_contact_info' && safetySettings.communicationSafety.linkPreventionEnabled) {
      return {
        id: `warning_${Date.now()}`,
        type: 'safety_reminder',
        severity: 'medium',
        title: 'Contact Info Warning',
        message: 'Be cautious when sharing personal contact information with new matches.',
        timestamp: new Date(),
        isRead: false,
        isResolved: false,
        actions: [
          { id: 'proceed', label: 'Send Anyway', type: 'dismiss', severity: 'medium' },
          { id: 'tips', label: 'Safety Tips', type: 'warn', severity: 'low' }
        ]
      };
    }

    return null;
  };

  const getUnreadAlertsCount = (): number => {
    return alerts.filter(alert => !alert.isRead && !alert.isResolved).length;
  };

  const getPendingReportsCount = (): number => {
    return reports.filter(report => report.status === 'pending').length;
  };

  const getSafetyScore = (): number => {
    // Calculate safety score based on settings and behavior
    let score = 50; // Base score
    
    // Boost for enabled safety features
    if (safetySettings.contentFiltering.enabled) score += 10;
    if (safetySettings.locationSafety.hideExactLocation) score += 10;
    if (safetySettings.communicationSafety.linkPreventionEnabled) score += 10;
    if (safetySettings.meetupSafety.publicPlaceReminders) score += 10;
    if (safetySettings.privacySettings.restrictUnverifiedContacts) score += 10;
    
    // Penalty for security issues
    const unresolvedAlerts = alerts.filter(a => !a.isResolved && a.severity === 'high').length;
    score -= unresolvedAlerts * 15;
    
    return Math.max(0, Math.min(100, score));
  };

  const value = {
    alerts,
    reports,
    contentModerations,
    safetySettings,
    createAlert,
    markAlertAsRead,
    resolveAlert,
    dismissAlert,
    reportUser,
    reportContent,
    flagContent,
    moderateContent,
    blockUser,
    unblockUser,
    hideContent,
    restrictUser,
    updateSafetySettings,
    isUserBlocked,
    isContentHidden,
    shouldWarnUser,
    getUnreadAlertsCount,
    getPendingReportsCount,
    getSafetyScore
  };

  return (
    <SafetyContext.Provider value={value}>
      {children}
    </SafetyContext.Provider>
  );
};

export default SafetyContext;